import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button, Form, Modal, Row } from "react-bootstrap";
import { Typeahead } from "react-bootstrap-typeahead";
import { Option } from "react-bootstrap-typeahead/types/types";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import QueryKeys from "../../../../../api/queryKeys";
import { weekendApi } from "../../../../../api/weekend";
import { HGBugsnagNotify } from "../../../../../helpers/bugsnag";
import { getStatusCode } from "../../../../../helpers/errors";
import { NBSP } from "../../../../../helpers/util";
import { updateSpeakerCache } from "../../../../../query/meetings";
import { Congregation, PublicSpeakerInput, PublicTalk, Speaker } from "../../../../../types/scheduling/weekend";
import { UserAppt } from "../../../../../types/user";
import { CongName, ShowStatus, Status } from "../../../common";

type CongOption = { id: number; name: string };

export function QuickAddSpeakerModal(props: {
  show: boolean;
  setShow: (show: boolean) => void;
  langGroupId: number;
  congregations: Congregation[];
  onSpeakerAdded: (speaker: Speaker, congId: number) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [selectedCong, setSelectedCong] = useState<Congregation | null>(null);
  const [typedCongName, setTypedCongName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [middleName, setMiddleName] = useState("");
  const [lastName, setLastName] = useState("");
  const [suffixName, setSuffixName] = useState("");
  const [email, setEmail] = useState("");
  const [cellphone, setCellphone] = useState("");
  const [homephone, setHomephone] = useState("");
  const [otherphone, setOtherphone] = useState("");
  const [appt, setAppt] = useState("");
  const [publicTalks, setPublicTalks] = useState<PublicTalk[]>([]);
  const [status, setStatus] = useState(Status.Null);
  const [showError, setShowError] = useState(false);

  const talksQuery = useQuery({
    queryKey: [QueryKeys.WMTalks],
    queryFn: () => weekendApi.getTalks(undefined, props.langGroupId),
  });

  const congOptions: CongOption[] = props.congregations.map((c) => ({
    id: c.id,
    name: CongName(c),
  }));

  const handleClose = () => {
    props.setShow(false);
    setSelectedCong(null);
    setTypedCongName("");
    setFirstName("");
    setMiddleName("");
    setLastName("");
    setSuffixName("");
    setEmail("");
    setCellphone("");
    setHomephone("");
    setOtherphone("");
    setAppt("");
    setPublicTalks([]);
    setStatus(Status.Null);
    setShowError(false);
  };

  const handleSave = async () => {
    if (!firstName || !lastName) {
      setShowError(true);
      return;
    }
    setStatus(Status.Pending);
    const input: PublicSpeakerInput = {
      id: 0,
      congregation: selectedCong?.id ?? null,
      congregation_name: selectedCong ? null : typedCongName || null,
      firstname: firstName,
      lastname: lastName,
      middlename: middleName || null,
      suffix: suffixName || null,
      email: email || null,
      cellphone: cellphone || null,
      homephone: homephone || null,
      otherphone: otherphone || null,
      appt: appt || null,
      out: true,
      public_talks: publicTalks,
    };
    try {
      const savedSpeaker = await weekendApi.setSpeaker(input, undefined, props.langGroupId);
      const congId = selectedCong?.id ?? -1;
      updateSpeakerCache(queryClient, congId, props.langGroupId, { id: 0 } as Speaker, savedSpeaker);
      props.onSpeakerAdded(savedSpeaker, congId);
      handleClose();
    } catch (err: any) {
      if (getStatusCode(err) === 409) {
        const congId = selectedCong?.id ?? -1;
        await queryClient.invalidateQueries({ queryKey: [QueryKeys.WMSpeakers, congId, props.langGroupId] });
        handleClose();
      } else {
        setStatus(Status.Failure);
        console.error("error adding speaker", err);
        HGBugsnagNotify("quickAddWMSpeaker", err);
      }
    }
  };

  return (
    <Modal show={props.show} onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{t("schedules.weekend.add-speaker")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-4">
          <Form.Label>{t("list.congregation.title")}</Form.Label>
          <Typeahead
            id="quick-add-cong"
            options={congOptions}
            labelKey="name"
            flip
            maxResults={100}
            paginationText={t("general.more") + "…"}
            onChange={(selected: Option[]) => {
              const opt = selected[0] as CongOption | undefined;
              if (opt) {
                setSelectedCong(props.congregations.find((c) => c.id === opt.id) ?? null);
              } else {
                setSelectedCong(null);
              }
            }}
            onInputChange={(text) => {
              setTypedCongName(text);
              if (!text) setSelectedCong(null);
            }}
          />
        </Form.Group>

        <Row>
          <Form.Group className="mb-3 col-12 col-sm-6">
            <Form.Label>{t("general.first-name")}</Form.Label>
            <Form.Control value={firstName} onChange={(e) => setFirstName(e.target.value)} autoFocus={!firstName} />
          </Form.Group>

          <Form.Group className="mb-3 col-12 col-sm-6">
            <Form.Label>{t("userinfo.name.last")}</Form.Label>
            <Form.Control value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3 col-12 col-sm-8">
            <Form.Label>{t("userinfo.name.middle")}</Form.Label>
            <Form.Control value={middleName} onChange={(e) => setMiddleName(e.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3 col-12 col-sm-4">
            <Form.Label>{t("userinfo.name.suffix")}</Form.Label>
            <Form.Control value={suffixName} onChange={(e) => setSuffixName(e.target.value)} />
          </Form.Group>
        </Row>

        <Row>
          <Form.Group className="mb-3 col-12">
            <Form.Label>{t("userinfo.email")}</Form.Label>
            <Form.Control type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3 col-12 col-sm-4">
            <Form.Label>{t("userinfo.cellphone")}</Form.Label>
            <Form.Control value={cellphone} onChange={(e) => setCellphone(e.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3 col-12 col-sm-4">
            <Form.Label>{t("userinfo.homephone")}</Form.Label>
            <Form.Control value={homephone} onChange={(e) => setHomephone(e.target.value)} />
          </Form.Group>

          <Form.Group className="mb-3 col-12 col-sm-4">
            <Form.Label>{t("userinfo.otherphone")}</Form.Label>
            <Form.Control value={otherphone} onChange={(e) => setOtherphone(e.target.value)} />
          </Form.Group>
        </Row>

        <Row>
          <Form.Group className="col-12 col-md-3">
            <Form.Label>{t("userinfo.appointment.0")}</Form.Label>
            <Form.Select value={appt} onChange={(e) => setAppt(e.target.value)}>
              <option />
              <option value={UserAppt.Elder}>{t("recordcard.elder")}</option>
              <option value={UserAppt.MS}>{t("recordcard.ministerialservant")}</option>
            </Form.Select>
          </Form.Group>

          <Form.Group className="col-12 col-md-9">
            <Form.Label>{t("schedules.weekend.outlines")}</Form.Label>
            <Select
              className="react-select-container col mb-1"
              classNamePrefix="react-select"
              placeholder={t("schedules.weekend.outlines")}
              closeMenuOnSelect={false}
              isMulti={true}
              isClearable={false}
              styles={{
                //@ts-ignore weird type error with typescript 5.3.3
                menu: (s) => ({ ...s, zIndex: 1500 }),
              }}
              value={publicTalks
                .sort((a, b) => a.number - b.number)
                .map((pt) => ({ value: pt.number, label: `${pt.number}` }))}
              options={
                talksQuery.data
                  ?.sort((a, b) => a.number - b.number)
                  .map((pt) => ({
                    value: pt.number,
                    label: `${`${pt.number}`.padStart(3, NBSP)} — ${pt.title}`,
                  })) ?? []
              }
              onChange={(e) => {
                setPublicTalks(e.map((opt) => ({ number: opt.value } as PublicTalk)));
              }}
            />
          </Form.Group>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Form.Text hidden={!showError} className="warn_text">
          {t("schedules.weekend.add-speaker-first-last-required")}
        </Form.Text>
        <Button variant="secondary" onClick={handleClose}>
          {t("general.cancel")}
        </Button>
        <Button variant="primary" onClick={handleSave}>
          {t("general.save")}
        </Button>
        <ShowStatus status={status} />
      </Modal.Footer>
    </Modal>
  );
}
